import React from 'react';
import { ArrowDown, ArrowUp, Check, Eye, EyeOff, Loader2, Minus } from 'lucide-react';
import { rowKey } from './dataDictionaryUtils';

function Checkbox({ checked, indeterminate = false, onChange, label, disabled = false }) {
  const on = checked || indeterminate;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onChange?.(!checked, e);
      }}
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0062FB]/30 ${
        on ? 'border-[#0062FB] bg-[#0062FB] text-white' : 'border-zinc-300 bg-white hover:border-zinc-400'
      } disabled:opacity-40`}
    >
      {indeterminate ? <Minus size={11} strokeWidth={3} aria-hidden /> : checked ? <Check size={11} strokeWidth={3} aria-hidden /> : null}
    </button>
  );
}

function SortHeader({ label, field, sortBy, sortOrder, onSort, className = '', align = 'left' }) {
  const active = sortBy === field;
  const Icon = sortOrder === 'asc' ? ArrowUp : ArrowDown;
  const alignClass = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  return (
    <th className={`h-9 px-3 ${alignClass} ${className}`} aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.08em] ${
          active ? 'text-[#18181B]' : 'text-[#71717A] hover:text-[#18181B]'
        }`}
      >
        {label}
        {active ? <Icon size={12} aria-hidden /> : null}
      </button>
    </th>
  );
}

/**
 * Paginated, sortable, multi-selectable column list.
 * Clicking a row selects it (opens the detail panel); the checkbox toggles bulk selection.
 */
export default function ColumnList({
  rows,
  loading,
  error,
  selectedKey,
  onSelectRow,
  checkedKeys,
  onToggleChecked,
  onToggleAllOnPage,
  sortBy,
  sortOrder,
  onSort,
  showTable = false,
}) {
  const pageKeys = rows.map((r) => rowKey(r));
  const checkedOnPage = pageKeys.filter((k) => checkedKeys.has(k)).length;
  const allChecked = rows.length > 0 && checkedOnPage === rows.length;
  const someChecked = checkedOnPage > 0 && !allChecked;

  return (
    <div className="relative min-h-0 flex-1 overflow-auto">
      {loading && rows.length > 0 ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center pt-12">
          <span className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white/95 px-3 py-1 text-xs text-[#71717A] shadow-card">
            <Loader2 size={12} className="animate-spin" aria-hidden /> Updating…
          </span>
        </div>
      ) : null}
      <table className="w-full table-fixed border-collapse text-[13px]">
        <thead className="sticky top-0 z-[1] bg-white">
          <tr className="border-b border-hairline">
            <th className="h-9 w-10 pl-3 pr-0 text-left">
              <Checkbox
                checked={allChecked}
                indeterminate={someChecked}
                onChange={() => onToggleAllOnPage(!allChecked)}
                label={allChecked ? 'Clear selection on this page' : 'Select all on this page'}
                disabled={rows.length === 0}
              />
            </th>
            <SortHeader label="Column" field="display_name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="w-[44%]" />
            {showTable ? (
              <SortHeader label="Table" field="table_name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="w-[22%]" />
            ) : null}
            <th className="h-9 px-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[#71717A]">Description</th>
            <SortHeader label="Visible" field="visible" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="w-16" align="center" />
            <SortHeader label="Done" field="complete" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="w-14" align="center" />
          </tr>
        </thead>
        <tbody>
          {error ? (
            <tr>
              <td colSpan={showTable ? 6 : 5} className="px-4 py-10 text-center text-sm text-red-600" role="alert">
                {error}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={showTable ? 6 : 5} className="px-4 py-12 text-center text-sm text-[#71717A]">
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" aria-hidden /> Loading columns…
                  </span>
                ) : (
                  'No columns match these filters.'
                )}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row);
              const selected = key === selectedKey;
              const checked = checkedKeys.has(key);
              const visible = row.visible === true;
              return (
                <tr
                  key={key}
                  onClick={() => onSelectRow(row)}
                  className={`h-11 cursor-pointer border-b border-[#EEF2F7] transition-colors ${
                    selected ? 'bg-primary-50' : checked ? 'bg-[#F5F9FF]' : 'hover:bg-[#F8FAFC]'
                  }`}
                  aria-selected={selected}
                >
                  <td className="pl-3 pr-0 align-middle">
                    <Checkbox
                      checked={checked}
                      onChange={(next, e) => onToggleChecked(row, next, e)}
                      label={`Select ${row.display_name || row.column_name}`}
                    />
                  </td>
                  <td className="px-3 align-middle">
                    <div className="flex min-w-0 flex-col leading-4">
                      <span className="truncate font-semibold text-[#18181B]">{row.display_name || row.column_name || '—'}</span>
                      <span className="truncate font-mono text-[11px] text-[#71717A]">{row.column_name}</span>
                    </div>
                  </td>
                  {showTable ? (
                    <td className="px-3 align-middle">
                      <div className="flex min-w-0 flex-col leading-4">
                        <span className="truncate text-[#3F3F46]">{row.table_name}</span>
                        <span className="truncate font-mono text-[11px] text-[#A1A1AA]">{row.schema}</span>
                      </div>
                    </td>
                  ) : null}
                  <td className="px-3 align-middle">
                    {row.description ? (
                      <span className="block truncate text-[#3F3F46]">{row.description}</span>
                    ) : (
                      <span className="text-xs text-amber-700">Missing description</span>
                    )}
                  </td>
                  <td className="px-3 text-center align-middle">
                    {visible ? (
                      <Eye size={17} className="inline text-[#18181B]" aria-label="Visible" />
                    ) : (
                      <EyeOff size={17} className="inline text-zinc-400" aria-label="Hidden" />
                    )}
                  </td>
                  <td className="px-3 text-center align-middle">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${row.complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      aria-label={row.complete ? 'Metadata complete' : 'Metadata incomplete'}
                      title={row.complete ? 'Metadata complete' : 'Missing display name, description, tags or folder'}
                    />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
